import React from 'react'
import { useTranslation } from 'react-i18next'
import { IconClockExclamation, IconMailFast } from '@tabler/icons-react'

import type { InviteStatus } from './utils'

// Single source of truth for how an invite status looks, shared with the
// Access-column chip in InvitedCell. That chip is a button with a chevron and
// its own wording ("Invite sent" vs this column's bare "Sent"), so it can't
// reuse the whole component — but it must not fork the palette, or the two
// views of the same invite drift apart on the next design tweak.
export const INVITE_STATUS_STYLES: Record<InviteStatus, string> = {
  sent: 'bg-amber-50 text-amber-700 border-amber-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
}

// Hover and chevron tints for the interactive (button) rendering.
export const INVITE_STATUS_HOVER: Record<InviteStatus, string> = {
  sent: 'hover:bg-amber-100',
  expired: 'hover:bg-red-100',
}

export const INVITE_STATUS_ACCENT: Record<InviteStatus, string> = {
  sent: 'text-amber-500',
  expired: 'text-red-400',
}

// Red + a clock for expired: it's the state that needs the admin to act.
export const INVITE_STATUS_ICONS: Record<InviteStatus, typeof IconMailFast> = {
  sent: IconMailFast,
  expired: IconClockExclamation,
}

// Chip + border are shared with the button rendering in InvitedCell.
export const INVITE_STATUS_CHIP_BASE = 'inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border'

export interface InviteStatusBadgeProps {
  status: InviteStatus
  className?: string
}

export const InviteStatusBadge: React.FC<InviteStatusBadgeProps> = ({ status, className }) => {
  const { t } = useTranslation('org', { keyPrefix: 'inviteStatus' })
  const Icon = INVITE_STATUS_ICONS[status]

  return (
    <span
      className={`${INVITE_STATUS_CHIP_BASE} whitespace-nowrap ${INVITE_STATUS_STYLES[status]} ${className || ''}`}
      data-qa={`invite-status-${status}`}
    >
      <Icon size={12} />
      {t(status)}
    </span>
  )
}

export default InviteStatusBadge
